import { patchResource } from './collections';
import { apiFetch, putBytes } from './http';
import type { UnrepricedHour } from './repricing';

/**
 * `/tenant-settings` (07 §4.5, 05a §3.2.1, `AM-173`): the tenant's own fiscal identity as invoice
 * issuer, its email sender, its support contact and its appearance - a single 1:1 record, no `id`,
 * read whole and patched whole, like `/entity-sequences` but without the per-key path segment.
 * The logo has its own two routes (`AM-174`, `tenantLogoApi` below) and never a field of a merge
 * patch here; `hasLogo`/`logoVersion` are read-only projections of it.
 */
export interface TenantSettings {
  fiscalLegalName: string | null;
  fiscalTaxId: string | null;
  fiscalTaxCondition: string | null;
  fiscalGrossIncomeId: string | null;
  /** ISO date (`yyyy-MM-dd`). */
  fiscalActivityStartDate: string | null;
  fiscalAddress: string | null;
  emailSenderName: string | null;
  emailSenderAddress: string | null;
  supportContact: string | null;
  /** `#RRGGBB`, or `null` when the tenant has not chosen one - the design's own `steel` applies. */
  primaryColor: string | null;
  /**
   * The day the tenant's week starts on, 0 (Sunday) to 6 (Saturday): the calendars of the console
   * and the portal and every "esta semana" start on it (`AM-361`). The session carries it too
   * (`activeTenant.firstDayOfWeek`), which is where the console reads it from.
   */
  firstDayOfWeek: number;
  knowledgeReviewPeriodDays: number;
  /** Migration 0052: the days a resolved ticket waits for its requester before it closes by itself (1..365). */
  autoCloseResolvedAfterDays: number;
  /** `AM-174`: whether `logo_storage_key` is set. The key itself is never in a response (`ISO-16`). */
  hasLogo: boolean;
  /** `AM-174`: a short hash of the key, `null` when there is none - the `?v=` the preview and `TenantMark` append to the public logo URL to bust a stale cache. */
  logoVersion: string | null;
  updatedByUserId: string | null;
  updatedAt: string;
  eTag: string;
}

/** A merge patch (RFC 7396): every field of {@link TenantSettings} but `hasLogo`/`logoVersion`/`updatedByUserId`/`updatedAt`/`eTag`, which the server derives. */
export type UpdateTenantSettingsInput = Partial<
  Omit<TenantSettings, 'hasLogo' | 'logoVersion' | 'updatedByUserId' | 'updatedAt' | 'eTag'>
>;

/**
 * `AM-511`: `GET`/`PATCH /tenant-settings/fiscal`, the screen "Datos fiscales del emisor": the six fields
 * with which the company issues, open to whoever configures the company or the billing. The `eTag` is
 * the row's, shared with {@link TenantSettings}.
 */
export interface TenantFiscalSettings {
  fiscalLegalName: string | null;
  fiscalTaxId: string | null;
  fiscalTaxCondition: string | null;
  fiscalGrossIncomeId: string | null;
  /** ISO date (`yyyy-MM-dd`). */
  fiscalActivityStartDate: string | null;
  fiscalAddress: string | null;
  updatedByUserId: string | null;
  updatedAt: string;
  eTag: string;
}

/** A merge patch (RFC 7396) of the six fiscal fields. */
export type UpdateTenantFiscalSettingsInput = Partial<
  Omit<TenantFiscalSettings, 'updatedByUserId' | 'updatedAt' | 'eTag'>
>;

/**
 * `GET /tenant-settings/appearance` (`13` §6.5, `AM-174`): what every signed-in member of the
 * tenant needs to render the console's theme and the portal's support line, tenant access only -
 * no `admin.tenant.configure`, the same as `/directory/*`.
 */
export interface TenantAppearance {
  primaryColor: string | null;
  supportContact: string | null;
  displayName: string;
  hasLogo: boolean;
  logoVersion: string | null;
}

export const tenantSettingsApi = {
  get: (signal?: AbortSignal): Promise<TenantSettings> =>
    apiFetch<TenantSettings>('/api/v1/tenant-settings', signal === undefined ? {} : { signal }),
  update: (body: UpdateTenantSettingsInput, etag: string): Promise<TenantSettings> =>
    patchResource<TenantSettings>('/api/v1/tenant-settings', body, etag),
  /** `AM-511`: "Datos fiscales del emisor", read and saved apart from the brand. */
  fiscal: (signal?: AbortSignal): Promise<TenantFiscalSettings> =>
    apiFetch<TenantFiscalSettings>(
      '/api/v1/tenant-settings/fiscal',
      signal === undefined ? {} : { signal },
    ),
  updateFiscal: (
    body: UpdateTenantFiscalSettingsInput,
    etag: string,
  ): Promise<TenantFiscalSettings> =>
    patchResource<TenantFiscalSettings>('/api/v1/tenant-settings/fiscal', body, etag),
  appearance: (signal?: AbortSignal): Promise<TenantAppearance> =>
    apiFetch<TenantAppearance>(
      '/api/v1/tenant-settings/appearance',
      signal === undefined ? {} : { signal },
    ),
  /** `AM-198`: whether every user of the tenant needs a second factor (08 §2.10). */
  security: (signal?: AbortSignal): Promise<TenantSecurityPolicy> =>
    apiFetch<TenantSecurityPolicy>(
      '/api/v1/tenant-settings/security',
      signal === undefined ? {} : { signal },
    ),
  updateSecurity: (body: TenantSecurityPolicy): Promise<TenantSecurityPolicy> =>
    apiFetch<TenantSecurityPolicy>('/api/v1/tenant-settings/security', { method: 'PUT', body }),
  /** `AM-202`: whether the tenant bills commercially and whether an hour needs a decision to be approved. */
  capabilities: (signal?: AbortSignal): Promise<TenantCapabilities> =>
    apiFetch<TenantCapabilities>(
      '/api/v1/tenant-settings/capabilities',
      signal === undefined ? {} : { signal },
    ),
  updateCapabilities: (body: UpdateTenantCapabilitiesInput): Promise<TenantCapabilitiesWritten> =>
    apiFetch<TenantCapabilitiesWritten>('/api/v1/tenant-settings/capabilities', {
      method: 'PUT',
      body,
    }),
  /**
   * `AM-631`: how many hours are still Pendientes, which "Opciones generales" says before turning "Exigir
   * aprobación de horas" off.
   */
  pendingHours: (signal?: AbortSignal): Promise<{ pendingHours: number }> =>
    apiFetch<{ pendingHours: number }>(
      '/api/v1/tenant-settings/capabilities/pending-hours',
      signal === undefined ? {} : { signal },
    ),
};

export interface TenantSecurityPolicy {
  mfaRequired: boolean;
}

/**
 * `AM-202`: turning `commercialBillingEnabled` off stops contracts, billing rules, periods,
 * billable entries and invoices (every commercial route answers `commercial_billing_disabled`) and
 * every hour is recorded not billable, whatever `isBillable` says - rate cards keep working, since
 * they value the cost of an hour, not what a client is charged. `timeApprovalRequired` off makes an
 * hour born approved, with no approver (`AM-203`, `TimeEntryDetail.approvalSource`).
 * `sendInvoiceOnIssue` (migration 0058): issuing a document also queues its email to the
 * organization's billing addresses; off unless the tenant turns it on. The `PUT` takes every flag
 * at once, so the page that edits them always sends the three. `AM-328`: it also takes
 * `defaultHourlyRate`, always (a number, or `null`): a `PUT` that omits it clears it on the server.
 */
export interface TenantCapabilities {
  commercialBillingEnabled: boolean;
  timeApprovalRequired: boolean;
  sendInvoiceOnIssue: boolean;
  /** `AM-328`: the price of an hour when no rate card answers for it and the organization has none of its own; `null` when the tenant has not set one. */
  defaultHourlyRate: number | null;
  /** `AM-328`: the tenant's currency (ISO 4217), read-only - the answer carries it, the `PUT` never sends it. */
  currency?: string | null;
  /**
   * `AM-510`: the general payment terms of the company, 0 to 365 days: a document falls due that many
   * days after its date when neither its contract nor its customer has terms. Thirty until the tenant
   * changes them; a `PUT` that omits it sets it back to thirty, so the page always sends it.
   */
  generalPaymentTermsDays: number;
  /**
   * `AM-665`, `AM-633` (C30-28): only in the answer of a `PUT` that saved a new general price per hour: how many
   * hours "Sin precio" it priced again and the ones it could not, with why.
   */
  repricedHours?: number | null;
  notRepricedHours?: UnrepricedHour[] | null;
}

/**
 * What the `PUT` takes: every field of {@link TenantCapabilities} but the read-only `currency` (`AM-328`)
 * and the counts of the answer (`AM-665`, `AM-633`). `AM-631`: `approvePendingHours`, when it turns "Exigir
 * aprobación de horas" off, approves every hour still Pendiente ("Aprobada por: Sin revisión").
 */
export type UpdateTenantCapabilitiesInput = Omit<
  TenantCapabilities,
  'currency' | 'repricedHours' | 'notRepricedHours'
> & {
  approvePendingHours?: boolean;
};

/** `AM-631`: what the `PUT` answers: the settings and, when it approved them, how many hours were approved. */
export interface TenantCapabilitiesWritten extends TenantCapabilities {
  approvedHours?: number | null;
}

/** Historical behaviour: before this settled `AM-202`, every tenant billed commercially and required approval. */
export const DEFAULT_TENANT_CAPABILITIES: TenantCapabilities = {
  commercialBillingEnabled: true,
  timeApprovalRequired: true,
  sendInvoiceOnIssue: false,
  defaultHourlyRate: null,
  generalPaymentTermsDays: 30,
};

/** The three types `PUT /tenant-settings/logo` accepts (`15` §4.6, `AM-174`): narrower than the general allowlist of attachments - no `gif`, no `svg`. */
export const TENANT_LOGO_CONTENT_TYPE_BY_EXTENSION: Readonly<Record<string, string>> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
};

/** What `<input type="file" accept>` narrows the picker to. */
export const TENANT_LOGO_ACCEPT = Object.keys(TENANT_LOGO_CONTENT_TYPE_BY_EXTENSION)
  .map((extension) => `.${extension}`)
  .join(',');

/** `15` §4.6, `AM-174`: 1 MB, fixed - not the tenant-restrictable ceiling `Attachments:MaxSizeMb` gives the general upload. */
export const TENANT_LOGO_MAX_SIZE_BYTES = 1024 * 1024;

/**
 * `PUT`/`DELETE /tenant-settings/logo` (`AM-174`): the two-step upload of `07` §4.11 collapsed into
 * one `PUT` for a slot that carries no metadata of its own - the raw bytes, `Content-Type` the
 * media type the picker declared. A successful `PUT` does not answer the updated row: the caller
 * invalidates `['tenantSettings']` and the appearance query the same way a `PATCH` does.
 */
export const tenantLogoApi = {
  upload: (file: File, contentType: string): Promise<void> =>
    putBytes('/api/v1/tenant-settings/logo', file, contentType),
  remove: (): Promise<void> => apiFetch<void>('/api/v1/tenant-settings/logo', { method: 'DELETE' }),
};
